/**
 * Index component considered to be the root file of the app.
 */
// React imports
import React, {
    lazy,
    Suspense
} from "react";
import { createRoot } from "react-dom/client";
import type { Root } from "react-dom/client";
import { Provider } from "react-redux";

// Third-party library imports
import { Quill } from "react-quill-new";
import {
    ConfigProvider,
    App as AntdApp
} from "antd";
import "core-js/stable";

// Constants
import config, {
    configAmplify,
    Environments
} from "src/AmplifyConfig";

// Custom hooks or HOCs
import initializeAntdTheme from "./hooks/antd_theme/themeInitializing";
import { HubListenningProvider } from "./hooks/amplify_hub/HubListenningProvider";

// Project-specific modules - components
import TrackPageViews from "./TrackPageViews";
import { CustomBlock } from "./components/common/CustomBlock";
import LoadingDeer from "./components/common/LoadingDeer/LoadingDeer";

// Utility functions
import * as serviceWorker from "./serviceWorker";

// Redux actions
import store from "./store/store";

// styles
import "antd/dist/reset.css";
import "./less/main.less";

// assets
import "./fontawesomeIcons";

// local parameters
// configAmplify(config);
Quill.register(CustomBlock, true);
const App = lazy(() => import('src/components/App/App'));

interface IProps { 
    chidren?: React.ReactNode;
}

const Root: React.FC<IProps> = () => {    
    return (
        <Provider store={store}>
            <TrackPageViews />
            <Suspense
                fallback={
                    <LoadingDeer />
                }
            >
                <HubListenningProvider>
                    <App />
                </HubListenningProvider>
            </Suspense>
        </Provider>
    );
};

const container = document.getElementById('root')! as any;
if (!container._rootInstance) {
    container._rootInstance = createRoot(container);
}
const root = container._rootInstance;

root.render(
    <ConfigProvider theme={initializeAntdTheme}>
        <AntdApp>
            <Root />
        </AntdApp>
    </ConfigProvider>
);

// If you want your app to work offline and load faster, you can change
// unregister() to register() below. Note this comes with some pitfalls.
// Learn more about service workers: https://bing.com/search?q=service+workers
serviceWorker.unregister();
if (import.meta.env.VITE_STAGE !== Environments.DEV) {
    console.error = () => {};
    if (window) {
        window.onerror = function () {
            return true;
        };
    }
}

export { store };
